import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTexte } from '../../texte'

import { useThreads } from '../../hooks/queries'
import { PartnerRow } from '../chatlist/ChatList'
import { ReplyIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { Blattkopf } from '../../components/Blattkopf'

/**
 * Die Antworten der Mitlesenden — erreichbar aus dem Reise-Chat selbst.
 *
 * **Aus dem Betrieb, und es ist eine Lücke der schmalen Ansicht.** Am iPad und
 * am Rechner steht links die Liste: unter der Reise je Mitlesendem eine Zeile
 * mit seiner Zahl. Auf dem Telefon liegt diese Liste hinter dem offenen Chat
 * — wer schreibt, sieht nicht, dass jemand geantwortet hat. Gemeldet als:
 * „auf einem smartphone ist in der Chat-Übersicht des Autors nicht
 * ersichtlich, wenn es Nachrichten gibt."
 *
 * **Ein Knopf in der Kopfzeile mit der Zahl**, und dahinter dieselben Zeilen
 * wie in der Liste (`PartnerRow`) — nicht nachgebaut, sondern dieselbe
 * Komponente. Er steht auf **jedem** Schirm und nicht nur auf dem schmalen:
 * eine Oberfläche, die je nach Breite etwas anderes kann, ist zwei
 * Oberflächen.
 *
 * **Nur beim Autor, und nur im Reise-Chat.** Ein Mitlesender hat keine
 * Verläufe unter sich, und in einem Zweiergespräch gibt es nichts
 * aufzufächern.
 */
export function AntwortenKnopf({ tripId }: { tripId: number }) {
  const t = useTexte()
  const [offen, setOffen] = useState(false)
  const threads = useThreads()

  // Die Verläufe dieser Reise: alles, was einen Gegenüber hat. Dieselbe
  // Abfrage, die auch die Liste speist -- sie läuft ohnehin im Takt, also
  // kostet der Knopf keine zusätzliche Anfrage.
  const verlaeufe = (threads.data ?? []).filter(
    (t) => t.trip_id === tripId && t.with_user !== null,
  )
  const ungelesen = verlaeufe.reduce((summe, t) => summe + t.unread, 0)

  if (verlaeufe.length === 0) return null

  return (
    <>
      <button
        type="button"
        className="icon-button antworten__knopf"
        onClick={() => setOffen(true)}
        aria-label={
          ungelesen > 0 ? t.chat.antwortenUngelesen(ungelesen) : t.chat.antwortenName
        }
        title={t.chat.antworten}
      >
        <ReplyIcon />
        {/* Die Zahl sitzt am Knopf und nicht daneben: in der Kopfzeile ist
            kein Platz für ein zweites Element, und der Reisename wiegt
            schwerer. */}
        {ungelesen > 0 && <span className="antworten__zahl">{ungelesen}</span>}
      </button>

      {offen && <Antwortenblatt verlaeufe={verlaeufe} onZu={() => setOffen(false)} />}
    </>
  )
}

/**
 * Das Blatt selbst — eine Liste, mehr nicht.
 *
 * Gebaut wie die anderen Blätter (`.bogen`): fährt von unten herein, schließt
 * mit dem Kreuz und mit Escape. Ein Tipp auf eine Zeile führt in den Verlauf
 * — und damit auf eine andere Seite; das Blatt geht dabei ohnehin mit.
 *
 * **Am Seitenkörper, obwohl der Knopf in der Kopfzeile steht** (1.63.0). Der
 * Knopf gehört dorthin, das Blatt nicht: es lag im DOM in `<header
 * className="bar">`. Die Kopfzeile ist heute kein Bezugsrahmen (kein
 * `backdrop-filter`, kein `transform`) -- es klebte also am Fenster, und genau
 * das ist die Art Zusage, die still fällt, sobald jemand der Kopfzeile das
 * milchige Material gibt. Der Rang bleibt bei 1200: das Blatt geht aus der
 * Kopfzeile auf, wo kein anderes offen steht, und ein `bogen--obenauf` über
 * etwas, das nie da ist, wäre eine Behauptung.
 */
function Antwortenblatt({
  verlaeufe,
  onZu,
}: {
  verlaeufe: Parameters<typeof PartnerRow>[0]['thread'][]
  onZu: () => void
}) {
  const t = useTexte()

  useEscape(onZu)

  return createPortal(
    <div className="bogen" role="dialog" aria-modal="true" aria-label={t.chat.antworten}>
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <Blattkopf titel={t.chat.antworten} onSchliessen={onZu} />

        {/* **Kopf und Inhalt als Geschwister** (1.61.0). No foot here -- the
            head stays over the list of chats. */}
        <div className="bogen__rolle">
          <p className="bogen__hinweis">{t.chat.antwortenLead}</p>

          <div className="antworten__liste">
            {verlaeufe.map((thread) => (
              <PartnerRow key={`${thread.trip_id}:${thread.with_user?.id}`} thread={thread} />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
